import {
  shouldRetryGatewayRequest,
  waitForGatewayRetry,
} from "@/helper/gatewayRetry";

type VersionResponse = {
  version: string;
  builtAt?: string;
};

type AssetManifest = Record<string, unknown> & {
  entrypoints?: unknown;
  files?: Record<string, unknown>;
};

type ViteManifestEntry = {
  file?: unknown;
  isEntry?: unknown;
};

function normalizeAssetUrl(url: string): string {
  if (url.startsWith("/") || /^[a-z][a-z\d+.-]*:\/\//i.test(url)) {
    return url;
  }
  return `/${url}`;
}

function getManifestEntryScripts(manifest: AssetManifest): string[] {
  const candidates: string[] = [];

  if (Array.isArray(manifest.entrypoints)) {
    candidates.push(
      ...manifest.entrypoints.filter(
        (entry): entry is string => typeof entry === "string",
      ),
    );
  }

  const craMainFile = manifest.files?.["main.js"];
  if (typeof craMainFile === "string") {
    candidates.push(craMainFile);
  }

  for (const entry of Object.values(manifest)) {
    if (!entry || typeof entry !== "object") continue;

    const viteEntry = entry as ViteManifestEntry;
    if (viteEntry.isEntry === true && typeof viteEntry.file === "string") {
      candidates.push(viteEntry.file);
    }
  }

  return Array.from(new Set(candidates.map(normalizeAssetUrl))).filter((url) =>
    url.endsWith(".js"),
  );
}

async function requestVersion(retryCount = 0): Promise<Response> {
  const response = await fetch("/version.json", {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });

  if (shouldRetryGatewayRequest("get", response.status, retryCount)) {
    await waitForGatewayRetry();
    return requestVersion(retryCount + 1);
  }

  return response;
}

export async function fetchVersion(): Promise<string | null> {
  try {
    const resp = await requestVersion();
    if (!resp.ok) return null;
    const body = (await resp.json()) as VersionResponse;
    return body?.version ?? null;
  } catch {
    return null;
  }
}

export async function prefetchNextAssets() {
  if (process.env.NODE_ENV !== "production") return;
  try {
    const resp = await fetch("/asset-manifest.json", {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });
    if (!resp.ok) return;
    const manifest = (await resp.json()) as AssetManifest;

    for (const href of getManifestEntryScripts(manifest)) {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.as = "script";
      link.href = href;
      document.head.appendChild(link);
    }
  } catch {
    // ignore
  }
}
